import React, { useState } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';

// ---------------------------------------------------------

import Window from '@lib/window-size';
import { pushStateToQuery } from '@lib/query-utils';
import propertyTypeMap from '@src/data/property-type-map.json';
import { daysSinceOptions, priceOptions } from '@src/content/results/filters';
import {
  clearMultipleFilters,
  clearAllFilters,
  initialState
} from '@src/store/slices/searchFiltersSlice';

// ---------------------------------------------------------

import Button from '@components/button';
import FilterDropdownButton from './filter-button';
import Modal from '@components/modal';

import Checkboxes from '@components/checkbox/with-redux';
import RadioButtons from '@components/radio-button/with-redux';
import MultipleSelect from '@components/select/minmax-select/with-redux';
import Select from '@components/select/with-redux';
import RangeSlider from '@components/range-slider/with-redux';

// ---------------------------------------------------------

import styles from './styles.module.scss';
const {
  filter_apply_button,
  filter_clear_button,
  filters_grouping_header,
  filters_grouping,
  filter_header,
  filter_row,
  days_since_sold_container
} = styles;

// ---------------------------------------------------------

const Filters = () => {
  const router = useRouter();
  const dispatch = useDispatch();

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const reduxParams = useSelector((state) => state.searchFilters);

  // ---------------------------------------------------------

  const isMobile = Window().width <= 720;

  // ---------------------------------------------------------

  const handleClearingFilters = (filters) => {
    if (filters === undefined) {
      dispatch(clearAllFilters());
    } else {
      dispatch(clearMultipleFilters(filters));
    }
  };

  const handleApplyAllFilters = () => {
    pushStateToQuery(router, reduxParams, true);
    setModalIsOpen(false);
  };

  // ---------------------------------------------------------

  const applyAllButton = (
    <footer>
      <Button
        className={filter_apply_button}
        type="button"
        label="Apply Filters"
        onClick={handleApplyAllFilters}
      />
    </footer>
  );

  const clearButton = (labels) => (
    <button className={filter_clear_button} onClick={() => handleClearingFilters(labels)}>
      Clear filters
    </button>
  );

  const showingSolds = reduxParams.status.length === 1 && reduxParams.status.includes('Sold');
  // ---------------------------------------------------------

  const statusAndTypeFilters = (
    <>
      <div className={filter_row}>
        <Checkboxes
          label="Search types"
          group="propertyStatus"
          selectAllLabel="All Properties"
          items={[
            { label: 'Price Changes', value: 'daysSinceLastPriceChange' },
            { label: 'New Listings', value: 'daysSinceNew' },
            { label: 'Open Houses', value: 'showOnlyOpenHouses' },
            { label: 'New Construction', value: 'showOnlyNewConstructions' },
            { label: 'Featured', value: 'featured' }
          ]}
          layout="half"
        />
      </div>
      <div className={filter_row}>
        <Checkboxes
          label="Property status"
          group="status"
          items={[{ label: 'Active' }, { label: 'Pending' }, { label: 'Sold' }]}
          layout="half"
          autoUnselectOnAll
        />
        {showingSolds && (
          <Select
            className={days_since_sold_container}
            id="daysSinceSold"
            label="Sold in the last"
            options={daysSinceOptions}
          />
        )}
      </div>
      <div className={filter_row}>
        <RadioButtons
          additionalOptions
          label="Property type"
          groupName="propertyTypeId"
          items={propertyTypeMap}
        />
      </div>
    </>
  );

  const priceFilters = (
    <div className={filter_row}>
      <MultipleSelect
        label="Price"
        anyLabel="Any Price"
        id="price"
        defaultMin={initialState.minPrice}
        defaultMax={initialState.maxPrice}
        options={priceOptions}
      />
    </div>
  );

  const bedAndBathFilters = (
    <>
      <div className={filter_row}>
        <RadioButtons
          label="Bedrooms"
          groupName="bedrooms"
          theme="with-label-inside"
          items={[
            { label: 'Any', value: 0 },
            { label: '1+', value: 1 },
            { label: '2+', value: 2 },
            { label: '3+', value: 3 },
            { label: '4+', value: 4 },
            { label: '5+', value: 5 }
          ]}
        />
      </div>
      <div className={filter_row}>
        <RadioButtons
          label="Bathrooms"
          groupName="bathrooms"
          theme="with-label-inside"
          items={[
            { label: 'Any', value: 0 },
            { label: '1+', value: 1 },
            { label: '2+', value: 2 },
            { label: '3+', value: 3 },
            { label: '4+', value: 4 },
            { label: '5+', value: 5 }
          ]}
        />
      </div>
    </>
  );

  const additionalFilters = (
    <>
      <div className={filter_row}>
        <RangeSlider
          label="Square feet"
          id="squareFootage"
          marksSuffix=" ft"
          rangeMin={0}
          rangeMax={8000}
          interval={200}
        />
      </div>
      <div className={filter_row}>
        <RadioButtons
          label="Garage spaces"
          groupName="garageSpaces"
          theme="with-label-inside"
          items={[
            { label: 'Any', value: 0 },
            { label: '1+', value: 1 },
            { label: '2+', value: 2 },
            { label: '3+', value: 3 },
            { label: '4+', value: 4 }
          ]}
        />
      </div>
      <div className={filter_row}>
        <Checkboxes
          group="basementTypes"
          label="Basement"
          items={[
            { label: 'Finished', value: 'hasBasementTypeFinished' },
            { label: 'Partially Finished', value: 'hasBasementTypePartiallyFinished' },
            { label: 'Unfinished', value: 'hasBasementTypeUnfinished' }
          ]}
          layout="half"
        />
      </div>
      <div className={filter_row}>
        <Checkboxes
          group="heatingTypes"
          label="Heating type"
          items={[
            { label: 'Gas', value: 'hasHeatTypeGas' },
            { label: 'Electric', value: 'hasHeatTypeElectric' },
            { label: 'Steam', value: 'hasHeatTypeSteam' },
            { label: 'Forced air', value: 'hasHeatTypeForcedAir' },
            { label: 'Heat pump', value: 'hasHeatTypeHeatPump' },
            { label: 'Gravity', value: 'hasHeatTypeGravity' },
            { label: 'Geothermal', value: 'hasHeatTypeGeothermal' }
          ]}
          layout="half"
        />
      </div>

      <div className={filter_row}>
        <RangeSlider
          label="Lot size"
          id="acres"
          anyLabel="Any Size"
          marksSuffix=" ac"
          rangeMin={0}
          rangeMax={500}
          interval={25}
        />
      </div>

      <div className={filter_row}>
        <Checkboxes
          group="mustHaves"
          label="Must-haves"
          selectAllLabel={null}
          items={[
            { label: '1st floor bedroom', value: 'hasLevel1Bedroom' },
            { label: 'Formal dining', value: 'hasFormalDiningRoom' },
            { label: '1st floor bathroom', value: 'hasLevel1Bath' },
            { label: 'Living room', value: 'hasFamilyRoom' },
            { label: 'Swimming', value: 'hasSwimmingPool' },
            { label: 'Central air', value: 'hasCentralAir' }
          ]}
          layout="half"
        />
      </div>
    </>
  );

  const mobileFilters = (
    <div className={filters_grouping}>
      <header className={filters_grouping_header}>
        <h1>Filters</h1>
        {clearButton()}
      </header>
      {statusAndTypeFilters}
      {priceFilters}
      {bedAndBathFilters}
      {additionalFilters}
      {applyAllButton}
    </div>
  );

  // ---------------------------------------------------------

  if (isMobile) {
    return (
      <>
        <Button label="Add filters" onClick={() => setModalIsOpen(true)} />
        <Modal
          onModalClose={() => setModalIsOpen(false)}
          modalIsOpen={modalIsOpen}
          content={mobileFilters}
        />
      </>
    );
  }

  if (!isMobile) {
    return (
      <div className={filters_grouping}>
        <FilterDropdownButton label="Status & Type">
          <header className={filter_header}>
            {clearButton(['propertyStatus', 'status', 'propertyTypeId', 'propertySubSelectIds'])}
          </header>
          {statusAndTypeFilters}
        </FilterDropdownButton>

        <FilterDropdownButton label="Price">
          <header className={filter_header}>{clearButton(['minPrice', 'maxPrice'])}</header>
          {priceFilters}
        </FilterDropdownButton>

        <FilterDropdownButton label="Bed & Bath">
          <header className={filter_header}>{clearButton(['bedrooms', 'bathrooms'])}</header>
          {bedAndBathFilters}
        </FilterDropdownButton>

        <FilterDropdownButton label="Additional Filters">
          <header className={filter_header}>
            {clearButton([
              'minSquareFootage',
              'maxSquareFootage',
              'garageSpaces',
              'basementTypes',
              'heatingTypes',
              'minAcres',
              'maxAcres',
              'mustHaves'
            ])}
          </header>
          {additionalFilters}
        </FilterDropdownButton>
      </div>
    );
  }
};

Filters.propTypes = {};

export default Filters;
